import type { Api_Feed, FeedApiParams } from '@core/api'
import { logMessage, logWarning } from '@core/logging'

import type { FeedSourceConfig } from '../types'

type CreateFeedApi = (args: FeedApiParams) => Promise<Api_Feed>

/**
 * This is used to detect if the initial video in a feed.
 * When initialVideoId is set It creates a 2nd feed with the initial video ID.
 * If the initialVideoId not set or found it in the 1st feed, use the 1st feed.
 * If the initialVideoId is not found in the 1st feed, it uses the 2nd feed.
 * For safety, if 2nd feed is null, still use the 1st feed.
 */
export async function createFeedWithInitVideoDetect({
  config,
  createFeedApi,
  params,
}: {
  config: FeedSourceConfig
  createFeedApi: CreateFeedApi
  params: FeedApiParams
}) {
  return new Promise<Api_Feed>((resolve, reject) => {
    /**
     * check if a video exists in the feed.
     * @param feed - The feed to check.
     * @param videoId - The video ID to check for.
     * @returns true if the video exists in the feed, false otherwise.
     */
    function existInFeed(feed: Api_Feed | null, videoId: string): boolean {
      return !!feed?.feed_items?.find(
        (item) => item.video?.encoded_id === videoId,
      )
    }

    const paramsWithInitialVideoId = {
      ...params,
      firstVideoId: config.initialVideoId,
    }

    let firstResponse: Api_Feed | null = null
    let secondResponse: Api_Feed | null = null
    let promiseHandledCount = 0

    createFeedApi(params)
      .then(logFeedWarnings(params))
      .then((feed) => {
        firstResponse = feed
        promiseHandledCount++
        if (
          !config.initialVideoId ||
          existInFeed(feed, config.initialVideoId)
        ) {
          return resolve(feed)
        }
        if (secondResponse || promiseHandledCount > 1) {
          return resolve(secondResponse!)
        }
      })
      .catch((err) => reject(err))

    const initialVideoPromise = config.initialVideoId
      ? createFeedApi(paramsWithInitialVideoId).then(
          logFeedWarnings(paramsWithInitialVideoId),
        )
      : Promise.resolve(null)

    initialVideoPromise
      .then((feedWithInitVideoId) => {
        secondResponse = feedWithInitVideoId
        promiseHandledCount++
        if (!firstResponse) {
          return
        }
        if (
          !config.initialVideoId ||
          existInFeed(firstResponse, config.initialVideoId)
        ) {
          return resolve(firstResponse)
        } else if (existInFeed(secondResponse, config.initialVideoId)) {
          return resolve(secondResponse!)
        }

        return resolve(firstResponse)
      })
      .catch((err) => reject(err))
  })
}

/**
 * Check if the feed has any warnings. If so, log a warning to the console.
 * @param params - The feed parameters.
 * @returns a function that logs the feed warnings and returns the feed.
 */
export function logFeedWarnings(params: FeedApiParams) {
  return (feed: Api_Feed) => {
    if (feed?.warnings?.includes('channel_playlist_mismatch')) {
      logMessage(
        'Channel playlist mismatch warning',
        {
          captureContext: {
            tags: {
              channel: params.channel,
              playlist_id: params.playlistId,
            },
            extra: {
              channel: params.channel,
              playlist_id: params.playlistId,
            },
          },
        },
        1,
      )
      logWarning(
        `Channel playlist mismatch. Playlist ${params.playlistId} does not belong to channel ${params.channel}.`,
      )
    }
    return feed
  }
}
